import { Component, type ReactNode } from 'react';
import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';

// ─── ErrorBoundary ──────────────────────────────────────────────────────────
//
// Auffangnetz gegen den „weißen Bildschirm": stürzt eine Seite beim Rendern ab,
// zeigen wir eine verständliche Fehlermeldung statt einer leeren Fläche.
//
// Es gibt zwei Einstiegspunkte, die sich dieselbe Darstellung (ErrorFallback)
// teilen:
//   • RouteErrorBoundary  – als `errorElement` im Router. Fängt Render- und
//     Loader-Fehler innerhalb der Routen ab (der häufigste Fall).
//   • ErrorBoundary       – klassische React-Fehlergrenze. Umschließt den
//     RouterProvider und fängt alles ab, was außerhalb der Routen passiert
//     (Provider, Router-Initialisierung) und daher nicht bis zum
//     errorElement gelangt.

// ─── Gemeinsame Fallback-Darstellung ────────────────────────────────────────

interface ErrorFallbackProps {
  /** Menschlich lesbare Fehlermeldung (für Details). */
  message?: string;
}

function ErrorFallback({ message }: ErrorFallbackProps) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
        </div>

        <h1 className="mt-4 text-lg font-semibold text-slate-900">Etwas ist schiefgelaufen</h1>
        <p className="mt-2 text-sm text-slate-500">
          Die Ansicht konnte nicht geladen werden. Ein Neuladen behebt das Problem meistens.
        </p>

        {message && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-600">
              Technische Details
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-slate-50 p-3 text-left text-xs text-slate-600">
              {message}
            </pre>
          </details>
        )}

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            Seite neu laden
          </button>
          <Link
            to="/"
            onClick={() => {
              // Nach dem Weg zurück zur Startseite den fehlerhaften Zustand
              // sicher zurücksetzen.
              setTimeout(() => window.location.reload(), 0);
            }}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            <Home className="h-4 w-4" aria-hidden="true" />
            Zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Router-Variante (errorElement) ─────────────────────────────────────────

/** Als `errorElement` im Router registriert – fängt Fehler innerhalb der Routen. */
export function RouteErrorBoundary() {
  const error = useRouteError();

  let message: string | undefined;
  if (isRouteErrorResponse(error)) {
    message = `${error.status} ${error.statusText}`;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  // Fürs Debugging in der Konsole festhalten.
  // eslint-disable-next-line no-console
  console.error('Route-Fehler:', error);

  return <ErrorFallback message={message} />;
}

// ─── Klassische Fehlergrenze (um den RouterProvider) ────────────────────────

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  message?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : String(error),
    };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // eslint-disable-next-line no-console
    console.error('Nicht abgefangener Fehler:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback message={this.state.message} />;
    }
    return this.props.children;
  }
}
